import { useState } from 'react';

import { JxTooltip, JxHint } from '@jx';

import './UmlClassMemberTree.css';

const VISIBILITY_ORDER = ['+', '#', '~', '-'];
const VISIBILITY_LABELS = { '+': 'Public', '#': 'Protected', '~': 'Internal', '-': 'Private' };
const METHOD_SIGNATURE_RE = /^(\w+)\(([^)]*)\)(:\s*.+)?$/;

// Splits a method signature ("Name(params): ReturnType") into name/params/
// return-type spans so the method name stands out. Falls back to plain text
// for members that don't match (properties, fields without parens).
function renderMemberText(text) {
    const match = text.match(METHOD_SIGNATURE_RE);
    if (!match) {
        return text;
    }
    const [, name, params, returnType] = match;
    return (
        <>
            <span className="uml-member-method-name">{name}</span>
            <span className="uml-member-method-params">({params})</span>
            {returnType && <span className="uml-member-return-type">{returnType}</span>}
        </>
    );
}

/**
 * Groups class members by visibility (public/protected/internal/private),
 * alphabetically sorted within each group, collapsible per group. Toggle uses
 * onMouseDown + stopPropagation, not onClick — native click/dblclick proved
 * unreliable inside these shape widgets (see UmlShapeWidgetBase.js history),
 * and this also cleanly prevents the group header from starting a shape-drag.
 *
 * Constructors are rendered separately by UmlClassConstructorList (kept
 * outside this scrollable tree, see UmlClassWidget.jsx) — this component
 * only ever sees non-constructor members.
 * @param {{members: {id: string, visibility: string, text: string, memberKind: string}[]}} props
 */
export default function UmlClassMemberTree({ members }) {
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());

    const groups = VISIBILITY_ORDER.map(function buildGroup(visibility) {
        const groupMembers = members
            .filter(function matchVisibility(m) {
                return m.visibility === visibility && m.memberKind !== 'constructor';
            })
            .slice()
            .sort(function byName(a, b) {
                return a.text.localeCompare(b.text);
            });
        return { visibility, groupMembers };
    }).filter(function keepNonEmpty(group) {
        return group.groupMembers.length > 0;
    });

    const toggleGroup = (key, e) => {
        e.stopPropagation();
        setCollapsedGroups(function updateCollapsed(prev) {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    return (
        <div className="uml-class-member-tree">
            {groups.map(function renderGroup(group) {
                const isCollapsed = collapsedGroups.has(group.visibility);
                return (
                    <div className="uml-class-member-group" key={group.visibility}>
                        <div className="uml-class-member-group-header" onMouseDown={(e) => toggleGroup(group.visibility, e)}>
                            <span className="uml-class-member-group-arrow">{isCollapsed ? '\u25B8' : '\u25BE'}</span>
                            <span className="uml-class-member-group-label">{VISIBILITY_LABELS[group.visibility]}</span>
                            <span className="uml-class-member-group-count">{group.groupMembers.length}</span>
                        </div>
                        {!isCollapsed &&
                            group.groupMembers.map(function renderLeaf(member) {
                                const signature = `${member.visibility} ${member.text}`;
                                const hasSmells = member.smells && member.smells.length > 0;
                                const leafClassName = `uml-class-member-leaf${hasSmells ? ' uml-class-member-leaf--smell' : ''}`;
                                if (member.description || hasSmells) {
                                    return (
                                        <JxHint
                                            key={member.id}
                                            title={
                                                <>
                                                    <span className="jx-hint-signature">{signature}</span>
                                                    {member.description && (
                                                        <span className="jx-hint-description">{member.description}</span>
                                                    )}
                                                    {hasSmells && (
                                                        <span className="jx-hint-smells">
                                                            {member.smells.map(function renderSmell(smell, index) {
                                                                return (
                                                                    <span key={index} className="jx-hint-smell-line">
                                                                        {smell}
                                                                    </span>
                                                                );
                                                            })}
                                                        </span>
                                                    )}
                                                </>
                                            }
                                        >
                                            <div className={leafClassName}>
                                                <span className="uml-class-member-leaf-text">{renderMemberText(member.text)}</span>
                                            </div>
                                        </JxHint>
                                    );
                                }
                                return (
                                    <JxTooltip key={member.id} title={signature}>
                                        <div className={leafClassName}>
                                            <span className="uml-class-member-leaf-text">{renderMemberText(member.text)}</span>
                                        </div>
                                    </JxTooltip>
                                );
                            })}
                    </div>
                );
            })}
        </div>
    );
}
